<script setup>
import { watchEffect, computed, ref, onMounted } from 'vue';
import { useQuery } from 'villus';
import { useRoute } from 'vue-router';
import map from 'lodash-es/map';
import uniq from 'lodash-es/uniq';

import Flexible from '@/components/flexible/Flexible.vue';
import FlexibleQuery from '@/components/flexible/FlexibleQuery.js';
import PartnerWith from '@/components/partner-with/PartnerWith.vue';
import PartnerFragment from '@/graphql/PartnerFragment';
import PartnerLink from '@/components/partner-link/PartnerLink.vue';
import Share from '@/components/share/Share.vue';
import { useStore } from '@/store/Store.js';
import FeaturedGrid from '@/components/featured-grid/FeaturedGrid.vue';
import Newsletter from '@/components/newsletter/Newsletter.vue';
import Header from '@/components/header/Header.vue';
import AnchorBar from '@/components/anchor-bar/AnchorBar.vue';

const store = useStore();
const route = useRoute();
const routeSlug = route.params.slug;
const adFilters = ref({});
const variables = computed(() => ({
    lang: store.lang.value,
    slug: routeSlug,
    locale: `${store.lang.value}-CA`,
}));
const crumbs = ref([]);
const flexibleReady = ref(false);
const mounted = ref(false);
const anchors = ref([]);
const rootEl = ref(null);

const postFields = `
uri
title
typeHandle,
... on recette_Entry {
    category: recetteCategory { title },
    cover: recetteCover { title, url @transform (format: "webp", quality: 80, width: 800, height:426, immediately: true) },
}
... on blog_Entry {
    day: postDate @formatDateTime (format: "d"),
    month: postDate @formatDateTime (format: "n"),
    year: postDate @formatDateTime (format: "Y"),
    category: blogCategory { title },
    cover: blogCover { title, url @transform (format: "webp", quality: 80, width: 800, height:426, immediately: true) },
}
... on product_Entry {
    cover: flexibleImage { title, url @transform (format: "webp", quality: 80, width: 800, height:426, immediately: true) },
}
`;

const { data, isDone } = await useQuery({
    variables,
    query: `
        query FetchEntries($lang: [String], $slug: [String]) {
            blogs: entry(section: "blogs", site: $lang) {
                sectionHandle
                ... on blogs_Entry { 
                    subtitle: pancarteHautTitreBasSousPage
                    suptitle: pancarteHautTitreHautSousPage
                    supsubtitle: pancarteBasTitreHautSousPage
                    subsubtitle: pancarteBasTitreBasSousPage
                    personnageEntete: personnageEnteteSousPage {
                        inline
                    }
                }
            },
            entry (section: "blog", site: $lang, slug: $slug) {
                title,
                day: postDate @formatDateTime (format: "d"),
                month: postDate @formatDateTime (format: "n"),
                year: postDate @formatDateTime (format: "Y"),
                ... on blog_Entry {
                    category: blogCategory { url, id, ... on blogcategories_default_Entry { title } },
                    tags: blogTags { id },
                    ${PartnerFragment('partner')},
                    ${FlexibleQuery()},
                    source: blogSource,
                    ingredients: blogIngredients { id, title }
                    displayRelatedContents
                    relatedContents { 
                        ${postFields}
                    }
                }
            }
        }
    `,
});

if (data?.value?.entry?.category?.[0]?.url) {
    crumbs.value = [
        { url: '/blogue', text: 'Blogue' },
        { url: data.value.entry.category[0].url, text: data.value.entry.category[0].title },
    ];
}

let ings = [];
if (data?.value?.entry?.ingredients) {
    ings = ings.concat(map(data.value.entry.ingredients, 'id'));
}
adFilters.value.relatedTo = ings;
adFilters.value.contentType = 'recette';
if (data?.value?.entry?.type && data?.value?.entry?.type?.length !== 0) {
    adFilters.value.relatedTo = adFilters.value.relatedTo.concat(map(data.value.entry.type, 'id'));
}

/**
 * Recommendations random
 */
const variablesRecommendation = computed(() => ({
    lang: store.lang.value,
    ingredientIds: ings,
}));

const qq2 = `
    query FetchAutomaticRelatedEntries($lang: [String], $ingredientIds: [QueryArgument]) {
        recommendations: entries(section: ["recette", "blog", "product"], site: $lang, relatedTo: $ingredientIds, orderBy:"RAND()", limit: 6) {
            ${postFields}
        }
    }
`;
const { data: dataRecommendation } = await useQuery({
    variables: variablesRecommendation,
    query: qq2,
});
/**
 * End recommendations random
 */

watchEffect(() => {
    if (isDone.value) {
        if (data.value && data.value.entry && data.value.entry.tags && data.value.entry.tags.length > 0) {
            if (!adFilters.value.relatedTo) {
                adFilters.value.relatedTo = [];
            }
            adFilters.value.relatedTo = adFilters.value.relatedTo.concat(map(data.value.entry.tags, 'id'));
        }
        if (data.value && data.value.entry && data.value.entry.category && data.value.entry.category.length > 0) {
            if (!adFilters.value.relatedTo) {
                adFilters.value.relatedTo = [];
            }
            adFilters.value.relatedTo = adFilters.value.relatedTo.concat(map(data.value.entry.category, 'id'));
        }
        adFilters.value.relatedTo = uniq(adFilters.value.relatedTo);
        adFilters.value.contentType = 'blog';
    }
});

onMounted(() => {
    store.setHeaderColor({
        mainColor: 'indigo',
        theme: 'dark',
        wave: 'light-indigo',
        normal: {
            bg: 'white',
            txt: 'dark-indigo',
        },
        over: {
            bg: 'white',
            txt: 'dark-indigo',
        },
        prenav: {
            bg: 'bg-dark-indigo',
            txt: 'text-white',
        },
        nav: {
            txt: 'text-white',
            hover: 'bg-dark-indigo',
            burger: 'text-white',
            lines: 'text-dark-indigo',
        },
    });

    mounted.value = true;
});

const recommendations = computed(() => {
    let results = [];
    if (data?.value?.entry?.relatedContents?.length > 0) {
        results = data.value.entry.relatedContents;
    } else if (dataRecommendation?.value?.recommendations?.length > 0) {
        results = dataRecommendation.value.recommendations;
    }
    return results;
});
</script>

<template>
    <div
        ref="rootEl"
        class="Blog bg-bright-indigo"
        :class="[pageClasses]"
    >
        <Header
            :data="data?.blogs"
            color="dark-blue"
            title-color="dark-indigo"
            :title="data?.entry?.title"
            :suptitle="data?.entry?.day ? [data.entry.day, FRMonth[data.entry.month - 1], data.entry.year].join(String.fromCharCode(160)) : null"
            :crumbs="crumbs"
        />

        <AnchorBar
            v-if="flexibleReady && data && data?.entry && data?.entry?.flexible && anchors?.length"
            :anchors="anchors"
            unique-id="blog"
            :colors="{
                txt: 'white',
                bg: 'dark-indigo',
                ui: 'light-indigo',
                uiTxt: 'white',
            }"
        />

        <div class="px-6 text-center xl:px-20">
            <PartnerWith :data="data?.entry?.partner" />
        </div>
        <div class="mx-auto max-w-5xl">
            <Flexible
                :remove-space-bottom="true"
                :ad-filters="adFilters"
                :center="true"
                cover-color="blue"
                color="blue"
                theme-color="dark-blue"
                :data="data.entry.flexible"
                main-color="blue"
                v-on:flexible-ready="flexibleReady = true"
            />
            <PartnerLink :data="data?.entry?.partner" />

            <div class="Blog__source Page__right is-centered is-s mt-12 italic md:mt-24">
                <small>{{ data?.entry?.source }}</small>
            </div>

            <Share class="Page__right is-centered is-s" />
        </div>

        <Newsletter />

        <FeaturedGrid
            :tag="true"
            :posts="recommendations"
            title-top="D’autres contenus qui pourraient vous intéresser"
        />
    </div>
</template>
